import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteOrdersView,
    getRouteReportsSpendSummary,
} from 'shared/const/router';
import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { BackButton } from 'shared/ui/BackButton';
import {
    ReportDrillInMetrics,
    ReportDrillInSections,
} from 'shared/ui/ReportDrillInLayout';
import { ReportMetric } from 'shared/ui/ReportMetric';
import {
    ComposableReportWidget,
    type ReportWidgetInteractionPayload,
} from 'widgets/ComposableReportWidget';
import { Page } from 'widgets/Page';

import { SpendProductDrillInContextHeader } from './SpendProductDrillInContextHeader';

import { useReportsPageSpendSummaryProduct } from '../model/hooks/useReportsPageSpendSummaryProduct';
import { getProductPurchasesWidgetConfig } from '../model/lib/getProductPurchasesWidgetConfig';
import { mapProductPurchasesToWidgetItems } from '../model/lib/mapProductPurchasesToWidgetItems';

const ReportsPageSpendSummaryProduct = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        isSpendSummaryVisible,
        sku,
        product,
        loading,
        totalSpend,
        totalQuantity,
        ordersCount,
        totalSpendPercent,
        lastRequestParamsPresent,
        purchases,
        canLoadMorePurchases,
        loadMorePurchases,
    } = useReportsPageSpendSummaryProduct();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSpendSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const openOrderDetailsInNewTab = useCallback((orderId: string) => {
        window.open(
            getRouteOrdersView(orderId),
            '_blank',
            'noopener,noreferrer',
        );
    }, []);

    const handleOrderDetailsClick = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const orderId = payload.item.meta?.orderId;
            if (typeof orderId === 'string' && orderId) {
                openOrderDetailsInNewTab(orderId);
            }
        },
        [openOrderDetailsInNewTab],
    );

    if (isSpendSummaryVisible === undefined) {
        return null;
    }

    if (!isSpendSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!sku || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSpendSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-spend-summary-product"
            padding="24px 35px"
        >
            <Box>
                <BackButton
                    onBack={handleBack}
                    title={t('spendSummary.pdfTitle')}
                    id="back-to-spend-summary-button"
                />

                <SpendProductDrillInContextHeader sku={sku} product={product} />

                <ReportDrillInMetrics>
                    <ReportMetric
                        title={t('spendSummary.totalSpend')}
                        primary={formatReportCurrency(totalSpend)}
                        loading={loading}
                        minHeight="120px"
                    />
                    <ReportMetric
                        title={t('spendSummary.percentOfTotalSpend')}
                        primary={formatReportPercent(totalSpendPercent)}
                        loading={loading}
                        minHeight="120px"
                    />
                    <ReportMetric
                        title={t('spendSummary.productsMetricTotalQuantity')}
                        primary={totalQuantity.toLocaleString()}
                        loading={loading}
                        minHeight="120px"
                    />
                    <ReportMetric
                        title={t('spendSummary.productsMetricOrders')}
                        primary={ordersCount.toLocaleString()}
                        loading={loading}
                        minHeight="120px"
                    />
                </ReportDrillInMetrics>

                <ReportDrillInSections>
                    <ComposableReportWidget
                        config={getProductPurchasesWidgetConfig({
                            t,
                            canLoadMorePurchases,
                            loadMorePurchases,
                            onOrderDetailsClick: handleOrderDetailsClick,
                        })}
                        items={mapProductPurchasesToWidgetItems(
                            purchases?.items ?? [],
                        )}
                        loading={loading}
                    />
                </ReportDrillInSections>
            </Box>
        </Page>
    );
};

export default ReportsPageSpendSummaryProduct;
